import React, { useEffect } from 'react';
import { X, Check, RotateCcw, Loader2 } from 'lucide-react';

/**
 * Review modal for reapply-fabric results.
 * User accepts → parent puts image in the studio preview.
 * User rejects → result is discarded (never added to the carousel).
 */
const ReapplyFabricReviewModal = ({
  isOpen,
  beforeUrl,
  afterUrl,
  fabricName = 'Fabric',
  isGenerating = false,
  onAccept,
  onReject,
}) => {
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape' && !isGenerating) onReject?.();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, isGenerating, onReject]);

  if (!isOpen) return null;

  if (isGenerating) {
    return (
      <div
        className="fixed inset-0 z-[100] flex flex-col items-center justify-center gap-5 bg-black/75 backdrop-blur-md px-6 text-center"
        role="alertdialog"
        aria-busy="true"
        aria-live="assertive"
        aria-label="Reapplying fabric"
      >
        <div className="relative flex items-center justify-center">
          <div className="absolute w-20 h-20 rounded-full bg-gradient-to-br from-purple-500/30 to-pink-500/30 blur-xl" />
          <div className="relative w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center shadow-2xl">
            <Loader2 className="w-8 h-8 text-white animate-spin" />
          </div>
        </div>
        <div className="max-w-xs space-y-1.5">
          <p className="text-base sm:text-lg font-bold text-white">Reapplying fabric…</p>
          <p className="text-xs sm:text-sm text-purple-100/90">
            Mapping {fabricName} onto your garment. This can take up to a minute — please keep this screen open.
          </p>
        </div>
      </div>
    );
  }

  if (!afterUrl) return null;

  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center p-3 sm:p-6 bg-black/55 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label="Review reapplied fabric"
    >
      <div className="relative w-full max-w-4xl max-h-[92vh] flex flex-col rounded-2xl bg-white shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between gap-3 px-4 sm:px-5 py-3 sm:py-4 border-b border-gray-100">
          <div className="min-w-0">
            <h2 className="text-base sm:text-lg font-bold text-gray-900 truncate">
              Review fabric reapply
            </h2>
            <p className="text-xs sm:text-sm text-gray-500 mt-0.5">
              Accept to show this result in the preview, or reject to discard it.
            </p>
          </div>
          <button
            type="button"
            onClick={onReject}
            className="w-9 h-9 rounded-xl text-gray-500 hover:bg-gray-100 flex items-center justify-center flex-shrink-0"
            aria-label="Close and reject"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-3 sm:p-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <div className="rounded-xl border border-gray-200 overflow-hidden bg-gray-50">
              <div className="px-3 py-2 border-b border-gray-100 bg-white">
                <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Before</p>
              </div>
              <div className="aspect-[3/4] bg-gray-100 flex items-center justify-center">
                {beforeUrl ? (
                  <img
                    src={beforeUrl}
                    alt="Current garment"
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <span className="text-sm text-gray-400">No image</span>
                )}
              </div>
            </div>
            <div className="rounded-xl border border-purple-200 overflow-hidden bg-purple-50/40 ring-1 ring-purple-100">
              <div className="px-3 py-2 border-b border-purple-100 bg-white">
                <p className="text-xs font-semibold text-purple-700 uppercase tracking-wide">After</p>
              </div>
              <div className="aspect-[3/4] bg-gray-100 flex items-center justify-center">
                <img
                  src={afterUrl}
                  alt="Garment with fabric reapplied"
                  className="w-full h-full object-contain"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-end gap-2 px-4 sm:px-5 py-3 sm:py-4 border-t border-gray-100 bg-gray-50/80">
          <button
            type="button"
            onClick={onReject}
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold bg-white border border-gray-200 text-gray-700 hover:bg-gray-100 transition-colors"
          >
            <RotateCcw className="w-4 h-4" />
            Reject
          </button>
          <button
            type="button"
            onClick={onAccept}
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700 shadow-sm transition-all"
          >
            <Check className="w-4 h-4" />
            Accept
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReapplyFabricReviewModal;
